import { useCallback, useState } from 'react';

import { Search } from '@mui/icons-material';
import { IconButton, TableCell } from '@mui/material';

import { KOGTableRow } from '@/main/components/shared/kogBoxGrid/styles';
import KOGPaginationGrid from '@/main/components/shared/paginationGrid/paginationGrid';

import {
  RowHeaderTextCenter,
  RowHeaderTextLeft,
  TextEllipsis
} from '@/main/components/shared/styles';

import { PaginationRequest } from '@/main/models/common/paginationRequest';
import { PaginationResponse } from '@/main/models/common/paginationResponse';

import { getTranslateByKey } from '@/main/services/utils';

import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import HistoricalDetails, {
  HistoricalDetailsData
} from '@/main/components/shared/historicalGrid/historicalDetails';
import * as S from './styles';

export interface HistoricalGridItem {
  id: string;
  action: string;
  date: string;
}

interface IHistoricalGrid<
  TRequest extends PaginationRequest,
  TGridItem,
  TDetailResponse
> {
  request: TRequest;

  loadMore: (request: TRequest) => Promise<PaginationResponse<TGridItem>>;

  mapGridItem: (item: TGridItem) => HistoricalGridItem;

  loadDetails: (item: TGridItem) => Promise<TDetailResponse>;

  mapDetails: (response: TDetailResponse) => HistoricalDetailsData;
}

export default function HistoricalGrid<
  TRequest extends PaginationRequest,
  TGridItem,
  TDetailResponse
>({
  request,
  loadMore,
  mapGridItem,
  loadDetails,
  mapDetails
}: IHistoricalGrid<TRequest, TGridItem, TDetailResponse>) {
  const [dataGrid, setDataGrid] = useState<TGridItem[]>([]);

  const [openDetails, setOpenDetails] = useState(false);

  const [selectedItem, setSelectedItem] = useState<TGridItem | null>(null);

  const rowHeaders = [
    <RowHeaderTextLeft key={0}>
      {getTranslateByKey('information.action')}
    </RowHeaderTextLeft>,
    <RowHeaderTextLeft key={1}>
      {getTranslateByKey('information.dateHourUser')}
    </RowHeaderTextLeft>,
    <RowHeaderTextCenter key={2}>
      {getTranslateByKey('information.actions')}
    </RowHeaderTextCenter>
  ];

  const sizeColumns = ['45%', '45%', '10%'];

  const fetchData = useCallback(
    async (requestData: TRequest) => {
      return loadMore(requestData);
    },
    [loadMore]
  );

  function handleOpenDetails(item: TGridItem) {
    setSelectedItem(item);
    setOpenDetails(true);
  }

  function handleCloseDetails() {
    setOpenDetails(false);
    setSelectedItem(null);
  }

  return (
    <>
      <S.Container id="id-historicGrid-scrollable">
        <KOGPaginationGrid<TGridItem, TRequest>
          showTitleResults
          idScrollableTarget="#id-historicGrid-scrollable"
          dataGrid={dataGrid}
          setDataGrid={setDataGrid}
          formFilters={request}
          fetchMore={fetchData}>
          <BoxGrid
            sizeColumns={sizeColumns}
            rowHeaders={rowHeaders}
            marginCustom="0">
            {dataGrid.map(item => {
              const mapped = mapGridItem(item);
              return (
                <KOGTableRow
                  key={mapped.id}
                  $isSizeLong
                  $notClickable
                  $notHover>
                  <TableCell align="left">
                    <TextEllipsis title={mapped.action}>
                      {mapped.action}
                    </TextEllipsis>
                  </TableCell>

                  <TableCell align="left">
                    <TextEllipsis title={mapped.date}>
                      {mapped.date}
                    </TextEllipsis>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDetails(item)}>
                      <Search fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </KOGTableRow>
              );
            })}
          </BoxGrid>
        </KOGPaginationGrid>
      </S.Container>

      {selectedItem && (
        <HistoricalDetails
          open={openDetails}
          closeModal={handleCloseDetails}
          title={getTranslateByKey('information.historyDetails')}
          actionName={mapGridItem(selectedItem).action}
          loadDetails={() => loadDetails(selectedItem)}
          mapResponse={mapDetails}
        />
      )}
    </>
  );
}
